(() => {
  'use strict';

  const configNode = document.getElementById('DanielPremiumWheelConfig');
  if (!configNode) return;

  let config;
  try {
    config = JSON.parse(configNode.textContent || '{}');
  } catch (_) {
    return;
  }

  const text = config.text || {};
  const selector = {
    wrapper: '#ecomsend-widget .spinWheelWrapper',
    modal: '.ecomsend-SpinWheel__Modal__Container',
    content: '.ecomsend-SpinWheel__Modal__Content',
    dialog: '.ecomsend-SpinWheel__Modal__CustomDialog',
    nativeContent: '.ecomsend-SpinWheel__Modal__CustomDialogContent',
    wheel: '.ecomsend-SpinWheel__Modal__SpinWheelWrapper',
    close: '.ecomsend-SpinWheel__Modal__CloseButton',
    nativeError: '[class*="Modal__ErrTextMarkup"], [class*="Modal__ErrText"]',
  };
  const pendingKey = `${config.lockKey}-pending`;
  const pendingTtlMs = 2 * 60 * 1000;
  const subscribeEndpoint = '/api/shop/v2/customer/subscribe';
  const minSpinMs = 3000;
  const maxSpinMs = 4750;
  const submitTimeoutMs = 9000;
  const instanceId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const safeStorageGet = (key) => {
    try { return window.localStorage.getItem(key); } catch (_) { return null; }
  };

  const safeStorageSet = (key, value) => {
    try { window.localStorage.setItem(key, value); } catch (_) {}
  };

  const safeStorageRemove = (key) => {
    try { window.localStorage.removeItem(key); } catch (_) {}
  };

  const captureNextSubscribeResponse = (timeoutMs = 7000) => {
    if (typeof window.fetch !== 'function') {
      return { cancel: () => {}, promise: Promise.resolve(null) };
    }

    const originalFetch = window.fetch;
    let settled = false;
    let timer = 0;
    let resolveCapture;
    const promise = new Promise((resolve) => { resolveCapture = resolve; });
    const finish = (value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      if (window.fetch === wrappedFetch) window.fetch = originalFetch;
      resolveCapture(value);
    };
    const wrappedFetch = function (...args) {
      const input = args[0];
      const requestUrl = typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : String(input?.url || '');
      let responsePromise;
      try {
        responsePromise = originalFetch.apply(this, args);
      } catch (error) {
        if (requestUrl.includes(subscribeEndpoint)) finish({ payload: null, status: 0 });
        throw error;
      }
      if (requestUrl.includes(subscribeEndpoint)) {
        Promise.resolve(responsePromise).then(async (response) => {
          let payload = null;
          try { payload = await response.clone().json(); } catch (_) {}
          finish({ payload, status: Number(response.status || 0) });
        }).catch(() => finish({ payload: null, status: 0 }));
      }
      return responsePromise;
    };

    window.fetch = wrappedFetch;
    timer = window.setTimeout(() => finish(null), timeoutMs);
    return { cancel: () => finish(null), promise };
  };

  const hasPendingLock = () => {
    const raw = safeStorageGet(pendingKey);
    if (!raw) return false;
    try {
      const value = JSON.parse(raw);
      if (Date.now() - Number(value.at || 0) < pendingTtlMs) return true;
    } catch (_) {}
    safeStorageRemove(pendingKey);
    return false;
  };

  const setPendingLock = () => {
    safeStorageSet(pendingKey, JSON.stringify({ at: Date.now(), version: config.version }));
    try {
      const channel = new BroadcastChannel('godhund-premium-wheel');
      channel.postMessage('pending');
      channel.close();
    } catch (_) {}
  };

  const clearPendingLock = () => safeStorageRemove(pendingKey);

  const cookieValue = (name) => {
    const prefix = `${encodeURIComponent(name)}=`;
    const row = document.cookie.split('; ').find((item) => item.startsWith(prefix));
    if (!row) return '';
    try { return decodeURIComponent(row.slice(prefix.length)); } catch (_) { return row.slice(prefix.length); }
  };

  const openLockDatabase = () => new Promise((resolve) => {
    if (!window.indexedDB) return resolve(null);
    try {
      const request = window.indexedDB.open('godhund-premium-wheel', 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains('state')) {
          request.result.createObjectStore('state');
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    } catch (_) {
      resolve(null);
    }
  });

  const indexedLock = async () => {
    const db = await openLockDatabase();
    if (!db) return false;
    return new Promise((resolve) => {
      try {
        const request = db.transaction('state', 'readonly').objectStore('state').get(config.lockKey);
        request.onsuccess = () => resolve(Boolean(request.result));
        request.onerror = () => resolve(false);
      } catch (_) {
        resolve(false);
      }
    });
  };

  const setIndexedLock = async (value) => {
    const db = await openLockDatabase();
    if (!db) return;
    try {
      db.transaction('state', 'readwrite').objectStore('state').put(value, config.lockKey);
    } catch (_) {}
  };

  const cartLock = async () => {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return false;
      const cart = await response.json();
      return String(cart?.attributes?.[config.cartAttribute] || '') === '1';
    } catch (_) {
      return false;
    }
  };

  const setCartLock = async () => {
    try {
      await fetch(window.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [config.cartAttribute]: '1' } }),
      });
    } catch (_) {}
  };

  const hasLock = async () => Boolean(
    safeStorageGet(config.lockKey)
    || cookieValue(config.lockKey)
    || await indexedLock()
    || await cartLock()
  );

  const setLock = () => {
    const value = JSON.stringify({ at: new Date().toISOString(), version: config.version });
    safeStorageSet(config.lockKey, value);
    document.cookie = `${encodeURIComponent(config.lockKey)}=1; Max-Age=31536000; Path=/; SameSite=Lax; Secure`;
    setIndexedLock(value);
    setCartLock();
    try {
      const channel = new BroadcastChannel('godhund-premium-wheel');
      channel.postMessage({ source: instanceId, type: 'locked' });
      channel.close();
    } catch (_) {}
  };

  const setReactInputValue = (input, value) => {
    if (!input) return;
    const prototype = input instanceof HTMLTextAreaElement
      ? window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement.prototype;
    const descriptor = Object.getOwnPropertyDescriptor(prototype, 'value');
    if (descriptor?.set) descriptor.set.call(input, value);
    else input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const setReactChecked = (input, checked) => {
    if (!input) return;
    const descriptor = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'checked');
    if (descriptor?.set) descriptor.set.call(input, checked);
    else input.checked = checked;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const popupId = (wrapper) => {
    if (wrapper.dataset.danielWheelPopupId) return wrapper.dataset.danielWheelPopupId;
    const button = wrapper.querySelector('[id^="ecomsend-submit-button-"]');
    const id = button?.id?.replace('ecomsend-submit-button-', '') || '';
    if (id) wrapper.dataset.danielWheelPopupId = id;
    return id;
  };

  const rewardFromCookie = (id) => {
    const raw = cookieValue(`reward${id}`) || cookieValue(`preview_reward${id}`);
    if (!raw) return null;
    const candidates = [raw];
    try { candidates.push(decodeURIComponent(raw)); } catch (_) {}
    for (const candidate of candidates) {
      try {
        const parsed = JSON.parse(candidate);
        if (parsed && typeof parsed === 'object') return parsed;
      } catch (_) {}
    }
    return null;
  };

  const chanceRows = () => [
    ['oddsDiscount15', config.odds.discount15],
    ['oddsShipping', config.odds.shipping],
    ['oddsNone', config.odds.none],
    ['oddsDiscount25', config.odds.discount25],
    ['oddsDiscount10', config.odds.discount10],
  ].map(([label, chance]) => `<li><span>${escapeHtml(text[label])}</span><strong>${String(chance).replace('.', ',')} %</strong></li>`).join('');

  const markup = () => `
    <div class="daniel-wheel-ui notranslate" translate="no" data-daniel-wheel-ui>
      <section class="daniel-wheel-ui__start" data-daniel-wheel-start>
        <div class="daniel-wheel-ui__hero">
          <p class="daniel-wheel-ui__eyebrow">${escapeHtml(text.eyebrow)}</p>
          <h2 class="daniel-wheel-ui__heading" id="DanielWheelHeading">${escapeHtml(text.heading)}</h2>
          <p class="daniel-wheel-ui__description">${escapeHtml(text.description)}</p>
        </div>
        <form class="daniel-wheel-ui__form-band" data-daniel-wheel-form novalidate>
          <div class="daniel-wheel-ui__fields">
            <div class="daniel-wheel-ui__field">
              <label for="DanielWheelFirstName">${escapeHtml(text.firstName)}</label>
              <input id="DanielWheelFirstName" name="first_name" type="text" autocomplete="given-name" required placeholder="${escapeHtml(text.firstNamePlaceholder)}">
            </div>
            <div class="daniel-wheel-ui__field">
              <label for="DanielWheelEmail">${escapeHtml(text.email)}</label>
              <input id="DanielWheelEmail" name="email" type="email" inputmode="email" autocomplete="email" required placeholder="${escapeHtml(text.emailPlaceholder)}">
            </div>
          </div>
          <div class="daniel-wheel-ui__action-row">
            <p class="daniel-wheel-ui__consent">${escapeHtml(text.consent)}</p>
            <button class="daniel-wheel-ui__submit" type="submit" data-daniel-wheel-submit>${escapeHtml(text.submit)}</button>
          </div>
          <p class="daniel-wheel-ui__error" role="alert" aria-live="polite" data-daniel-wheel-error></p>
          <div class="daniel-wheel-ui__footer">
            <span>${escapeHtml(text.footer)}</span>
            <span aria-hidden="true">·</span>
            <button type="button" data-daniel-wheel-odds-open>${escapeHtml(text.showOdds)}</button>
          </div>
        </form>
      </section>
      <section class="daniel-wheel-ui__result" data-daniel-wheel-result hidden>
        <p class="daniel-wheel-ui__eyebrow">${escapeHtml(text.resultEyebrow)}</p>
        <h2 class="daniel-wheel-ui__result-heading" data-daniel-wheel-result-heading></h2>
        <p class="daniel-wheel-ui__result-description" data-daniel-wheel-result-description></p>
        <div class="daniel-wheel-ui__code-row" data-daniel-wheel-code-row hidden>
          <input class="daniel-wheel-ui__code" data-daniel-wheel-code readonly aria-label="${escapeHtml(text.resultEyebrow)}">
          <button class="daniel-wheel-ui__copy" type="button" data-daniel-wheel-copy>${escapeHtml(text.copy)}</button>
        </div>
        <a class="daniel-wheel-ui__result-action" href="/collections/all" data-daniel-wheel-result-action>${escapeHtml(text.continue)}</a>
      </section>
      <section class="daniel-wheel-ui__odds" data-daniel-wheel-odds hidden aria-labelledby="DanielWheelOddsHeading">
        <div class="daniel-wheel-ui__odds-head">
          <h3 id="DanielWheelOddsHeading">${escapeHtml(text.oddsHeading)}</h3>
          <button class="daniel-wheel-ui__odds-close" type="button" aria-label="${escapeHtml(text.closeOdds)}" data-daniel-wheel-odds-close>×</button>
        </div>
        <p class="daniel-wheel-ui__odds-note">${escapeHtml(text.oddsNote)}</p>
        <ul class="daniel-wheel-ui__odds-list">${chanceRows()}</ul>
      </section>
    </div>`;

  const validName = (value) => String(value || '').trim().length > 0;
  const validEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/u.test(String(value || '').trim());

  const wheelSectors = () => [
    { key: 'discount15', label: text.oddsDiscount15, share: 0.20, color: '#6f95a7' },
    { key: 'discount25', label: text.oddsDiscount25, share: 0.20, color: '#d66f66' },
    { key: 'discount10', label: text.oddsDiscount10, share: 0.20, color: '#0f4a3c' },
    { key: 'none', label: text.oddsNone, share: 0.20, color: '#4f3a31' },
    { key: 'shipping', label: text.oddsShipping, share: 0.20, color: '#dce8ec', darkText: true },
  ];

  const labelLines = (label) => {
    const value = String(label || '').trim();
    const percentage = value.match(/^(\d+\s*%)(?:\s+)(.+)$/u);
    if (percentage) return [percentage[1], percentage[2]];
    const words = value.split(/\s+/u);
    if (words.length <= 1) return words;
    const split = Math.ceil(words.length / 2);
    return [words.slice(0, split).join(' '), words.slice(split).join(' ')];
  };

  const drawWheel = (canvas, phase = 0) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const size = canvas.width;
    const center = size / 2;
    const radius = size * 0.46;
    const sectors = wheelSectors();
    let angle = -Math.PI / 2;
    ctx.clearRect(0, 0, size, size);

    sectors.forEach((sector) => {
      const end = angle + sector.share * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(center, center);
      ctx.arc(center, center, radius, angle, end);
      ctx.closePath();
      if (sector.key === 'discount25') {
        const offset = (phase * 1.8 - 0.4) * size;
        const gradient = ctx.createLinearGradient(offset, 0, offset + size * 0.55, size);
        gradient.addColorStop(0, '#073a2f');
        gradient.addColorStop(0.38, '#0f4a3c');
        gradient.addColorStop(0.50, '#d8bd7f');
        gradient.addColorStop(0.60, '#176d54');
        gradient.addColorStop(1, '#073a2f');
        ctx.fillStyle = gradient;
      } else {
        ctx.fillStyle = sector.color;
      }
      ctx.fill();
      ctx.strokeStyle = sector.key === 'discount25' ? 'rgba(224, 193, 126, 0.95)' : 'rgba(255, 255, 255, 0.84)';
      ctx.lineWidth = sector.key === 'discount25' ? 5 : 3;
      ctx.stroke();

      const middle = angle + (end - angle) / 2;
      const labelRadius = sector.key === 'discount25' ? radius * 0.70 : radius * 0.61;
      const x = center + Math.cos(middle) * labelRadius;
      const y = center + Math.sin(middle) * labelRadius;
      const lines = labelLines(sector.label);
      ctx.fillStyle = sector.darkText ? '#30241e' : '#ffffff';
      ctx.font = `700 ${sector.key === 'discount25' ? 27 : 34}px Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      lines.forEach((line, row) => {
        ctx.fillText(line, x, y + (row - (lines.length - 1) / 2) * 38);
      });
      angle = end;
    });

    ctx.beginPath();
    ctx.arc(center, center, radius + 22, 0, Math.PI * 2);
    ctx.strokeStyle = '#f5f2eb';
    ctx.lineWidth = 36;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(center, center, radius + 7, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(216, 199, 167, 0.92)';
    ctx.lineWidth = 4;
    ctx.stroke();
  };

  const hideNativeContent = (nativeContent) => {
    if (!nativeContent) return;
    nativeContent.setAttribute('aria-hidden', 'true');
    nativeContent.querySelectorAll('a[href], button, input, select, textarea, [role="button"]').forEach((node) => {
      if (!node.hasAttribute('data-daniel-wheel-tabindex')) {
        node.setAttribute('data-daniel-wheel-tabindex', node.getAttribute('tabindex') ?? '');
      }
      node.setAttribute('tabindex', '-1');
    });
  };

  const restoreNativeContent = (nativeContent) => {
    if (!nativeContent) return;
    nativeContent.removeAttribute('aria-hidden');
    nativeContent.removeAttribute('inert');
    nativeContent.querySelectorAll('[data-daniel-wheel-tabindex]').forEach((node) => {
      const value = node.getAttribute('data-daniel-wheel-tabindex');
      if (value) node.setAttribute('tabindex', value);
      else node.removeAttribute('tabindex');
      node.removeAttribute('data-daniel-wheel-tabindex');
    });
  };

  const stopShimmer = () => {};

  const startShimmer = (wrapper, visual) => {
    const canvas = visual.querySelector('canvas');
    if (!canvas) return;
    drawWheel(canvas, 0.48);
  };

  const createCustomWheel = (wrapper) => {
    const host = wrapper.querySelector(selector.wheel);
    if (!host) return null;
    [...host.children].forEach((node) => {
      if (!node.classList.contains('daniel-wheel-visual')) node.setAttribute('aria-hidden', 'true');
    });
    const existing = host.querySelector('.daniel-wheel-visual');
    if (existing) {
      startShimmer(wrapper, existing);
      return existing;
    }
    const visual = document.createElement('div');
    visual.className = 'daniel-wheel-visual';
    visual.setAttribute('aria-hidden', 'true');
    const center = config.logoUrl
      ? `<img src="${escapeHtml(config.logoUrl)}" alt="" width="44" height="44" decoding="async">`
      : 'GH';
    visual.innerHTML = `
      <div class="daniel-wheel-visual__rotator"><canvas width="720" height="720"></canvas></div>
      <div class="daniel-wheel-visual__center">${center}</div>
      <img class="daniel-wheel-visual__pointer" alt="" src="https://static.ecomsend.com/www/popups/spin-wheel/point.svg">`;
    host.appendChild(visual);
    const canvas = visual.querySelector('canvas');
    drawWheel(canvas, 0.48);
    startShimmer(wrapper, visual);
    return visual;
  };

  const startCustomWheel = (wrapper) => {
    const visual = wrapper.querySelector('.daniel-wheel-visual');
    const rotator = visual?.querySelector('.daniel-wheel-visual__rotator');
    if (!visual || !rotator || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    rotator.style.animation = '';
    rotator.style.transition = 'none';
    rotator.classList.add('is-spinning');
    visual.classList.add('is-spinning');
  };

  const stopCustomWheel = (wrapper) => {
    const visual = wrapper.querySelector('.daniel-wheel-visual');
    const rotator = visual?.querySelector('.daniel-wheel-visual__rotator');
    if (!visual || !rotator) return;
    const transform = window.getComputedStyle(rotator).transform;
    rotator.classList.remove('is-spinning');
    visual.classList.remove('is-spinning');
    rotator.style.animation = 'none';
    rotator.style.transition = 'none';
    if (transform && transform !== 'none') rotator.style.transform = transform;
    delete wrapper.dataset.danielWheelSpinning;
  };

  const sectorMiddleDegrees = (key) => {
    let angle = -90;
    for (const sector of wheelSectors()) {
      const span = sector.share * 360;
      if (sector.key === key) return angle + span / 2;
      angle += span;
    }
    return 0;
  };

  const settleCustomWheel = (wrapper, key, done, durationMs = 900) => {
    const visual = wrapper.querySelector('.daniel-wheel-visual');
    const rotator = visual?.querySelector('.daniel-wheel-visual__rotator');
    if (!visual || !rotator) return done();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const transform = window.getComputedStyle(rotator).transform;
    let current = 0;
    if (transform && transform !== 'none') {
      const matrix = new DOMMatrixReadOnly(transform);
      current = Math.atan2(matrix.b, matrix.a) * 180 / Math.PI;
    }
    rotator.classList.remove('is-spinning');
    visual.classList.remove('is-spinning');
    rotator.style.animation = 'none';
    rotator.style.transition = 'none';
    rotator.style.transform = `rotate(${current}deg)`;
    const middle = sectorMiddleDegrees(key === 'brand' ? 'none' : key);
    const normalized = ((-middle - current) % 360 + 360) % 360;
    const settleMs = reduced ? 0 : Math.max(120, Math.min(900, Number(durationMs) || 900));
    const target = current + (reduced ? normalized : 720 + normalized);
    window.requestAnimationFrame(() => {
      rotator.style.transition = reduced ? 'none' : `transform ${settleMs}ms cubic-bezier(0.12, 0.76, 0.12, 1)`;
      rotator.style.transform = `rotate(${target}deg)`;
      window.setTimeout(done, reduced ? 40 : settleMs + 30);
    });
  };

  const unexpectedHighDiscountReward = (reward) => {
    try {
      const serialized = JSON.stringify(reward || '');
      const percentage = serialized.match(/(\d+(?:[.,]\d+)?)\s*%/u);
      return percentage ? Number(percentage[1].replace(',', '.')) > 25 : false;
    } catch (_) {
      return false;
    }
  };

  const resultKey = (reward) => {
    const index = Number(reward?.reward_index);
    const key = Number.isInteger(index) ? config.rewardByIndex[index] || 'none' : 'none';
    return key === 'discount10' && unexpectedHighDiscountReward(reward) ? 'none' : key;
  };

  const applyResult = (wrapper, ui, reward, key, discountCode = '') => {
    const id = popupId(wrapper);
    const start = ui.querySelector('[data-daniel-wheel-start]');
    const result = ui.querySelector('[data-daniel-wheel-result]');
    const heading = ui.querySelector('[data-daniel-wheel-result-heading]');
    const description = ui.querySelector('[data-daniel-wheel-result-description]');
    const codeRow = ui.querySelector('[data-daniel-wheel-code-row]');
    const codeInput = ui.querySelector('[data-daniel-wheel-code]');
    const action = ui.querySelector('[data-daniel-wheel-result-action]');
    const nativeCode = discountCode || wrapper.querySelector('#Discount_code')?.value || cookieValue(`discount_code${id}`);

    heading.textContent = text[key] || text.none;
    description.textContent = key === 'none' || key === 'brand' ? text.noneDescription : text.resultDescription;
    start.hidden = true;
    result.hidden = false;
    if (nativeCode && key !== 'none' && key !== 'brand') {
      codeInput.value = nativeCode;
      codeRow.hidden = false;
      action.textContent = text.useReward;
      action.href = `/discount/${encodeURIComponent(nativeCode)}?redirect=/collections/all`;
    } else {
      codeRow.hidden = true;
      action.textContent = text.continue;
      action.href = '/collections/all';
    }
    clearPendingLock();
    setLock();
    wrapper.classList.add('daniel-wheel-has-result');
    ui.dataset.resultState = 'shown';
    delete wrapper.dataset.danielWheelSpinning;
    action.focus({ preventScroll: true });
  };

  const revealResult = (wrapper, ui) => {
    if (ui.dataset.resultState) return true;
    const id = popupId(wrapper);
    const reward = rewardFromCookie(id);
    if (!reward) return false;
    const key = resultKey(reward);
    ui.dataset.resultState = 'settling';
    const showResult = () => applyResult(wrapper, ui, reward, key);
    if (wrapper.dataset.danielWheelSpinning === 'true') {
      settleCustomWheel(wrapper, key, showResult);
    } else {
      showResult();
    }
    return true;
  };

  const trapFocus = (wrapper, event) => {
    if (event.key === 'Escape') {
      wrapper.querySelector(selector.close)?.click();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...wrapper.querySelectorAll('.daniel-wheel-ui button:not([hidden]):not(:disabled), .daniel-wheel-ui input:not([hidden]):not(:disabled), .daniel-wheel-ui a[href]:not([hidden]), .ecomsend-SpinWheel__Modal__CloseButton')]
      .filter((node) => node.getClientRects().length > 0);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const isWrapperActuallyOpen = (wrapper) => {
    if (!wrapper || wrapper.hidden || !wrapper.classList.contains('dialogVisible')) return false;
    const style = window.getComputedStyle(wrapper);
    const rect = wrapper.getBoundingClientRect();
    return style.display !== 'none'
      && style.visibility !== 'hidden'
      && style.opacity !== '0'
      && rect.width > 0
      && rect.height > 0;
  };

  const enhance = async (wrapper) => {
    if (wrapper.dataset.danielWheelEnhanced) return;
    wrapper.dataset.danielWheelEnhanced = 'pending';
    let modal;
    let dialog;
    let nativeContent;
    let ui;
    let form;
    let firstName;
    let email;
    let submit;
    let error;
    try {
      if (await hasLock() || hasPendingLock()) {
        wrapper.querySelector(selector.close)?.click();
        wrapper.hidden = true;
        wrapper.dataset.danielWheelEnhanced = 'locked';
        document.documentElement.classList.remove('daniel-wheel-open');
        return;
      }

      modal = wrapper.querySelector(selector.modal);
      dialog = wrapper.querySelector(selector.dialog);
      nativeContent = wrapper.querySelector(selector.nativeContent);
      if (!modal || !dialog || !nativeContent) {
        delete wrapper.dataset.danielWheelEnhanced;
        return;
      }
      popupId(wrapper);
      dialog.insertAdjacentHTML('beforeend', markup());
      ui = dialog.querySelector('[data-daniel-wheel-ui]');
      form = ui?.querySelector('[data-daniel-wheel-form]');
      firstName = ui?.querySelector('#DanielWheelFirstName');
      email = ui?.querySelector('#DanielWheelEmail');
      submit = ui?.querySelector('[data-daniel-wheel-submit]');
      error = ui?.querySelector('[data-daniel-wheel-error]');
      if (!ui || !form || !firstName || !email || !submit || !error) {
        throw new Error('Premium wheel markup did not initialize');
      }
      if (!createCustomWheel(wrapper)) {
        throw new Error('Premium wheel visual did not initialize');
      }

      wrapper.querySelector(selector.close)?.setAttribute('aria-label', text.close || 'Lukk');
      hideNativeContent(nativeContent);
      wrapper.classList.add('daniel-wheel-enhanced');
      wrapper.dataset.danielWheelEnhanced = 'true';
      document.documentElement.classList.toggle('daniel-wheel-open', isWrapperActuallyOpen(wrapper));
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-labelledby', 'DanielWheelHeading');
    } catch (_) {
      ui?.remove();
      restoreNativeContent(nativeContent);
      wrapper.classList.remove('daniel-wheel-enhanced');
      wrapper.dataset.danielWheelEnhanced = 'fallback';
      document.documentElement.classList.remove('daniel-wheel-open');
      return;
    }

    let pending = false;
    let pendingTimer = 0;
    let spinStopTimer = 0;
    let subscribeCapture = null;

    ui.querySelector('[data-daniel-wheel-odds-open]').addEventListener('click', () => {
      ui.querySelector('[data-daniel-wheel-odds]').hidden = false;
      ui.querySelector('[data-daniel-wheel-odds-close]').focus();
    });
    ui.querySelector('[data-daniel-wheel-odds-close]').addEventListener('click', () => {
      ui.querySelector('[data-daniel-wheel-odds]').hidden = true;
      ui.querySelector('[data-daniel-wheel-odds-open]').focus();
    });
    ui.querySelector('[data-daniel-wheel-copy]').addEventListener('click', async (event) => {
      const value = ui.querySelector('[data-daniel-wheel-code]').value;
      try {
        await navigator.clipboard.writeText(value);
        event.currentTarget.textContent = text.copied;
        window.setTimeout(() => { event.currentTarget.textContent = text.copy; }, 1800);
      } catch (_) {
        ui.querySelector('[data-daniel-wheel-code]').select();
      }
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (pending) return;
      error.textContent = '';
      if (!validName(firstName.value)) {
        error.textContent = text.invalidName;
        firstName.focus();
        return;
      }
      if (!validEmail(email.value)) {
        error.textContent = text.invalidEmail;
        email.focus();
        return;
      }
      const currentNativeContent = wrapper.querySelector(selector.nativeContent);
      hideNativeContent(currentNativeContent);
      const nativeFirstName = currentNativeContent?.querySelector('#first_name');
      const nativeEmail = currentNativeContent?.querySelector('#email');
      const nativeConsent = currentNativeContent?.querySelector('input[type="checkbox"]');
      const nativeSubmitContainer = currentNativeContent?.querySelector('[id^="ecomsend-submit-button-"]');
      const nativeSubmitControl = nativeSubmitContainer?.querySelector('[role="button"], button, .ecomsend__Button') || nativeSubmitContainer;
      if (!nativeFirstName || !nativeEmail || !nativeSubmitControl) {
        error.textContent = text.submitError;
        return;
      }
      setReactInputValue(nativeFirstName, firstName.value.trim());
      setReactInputValue(nativeEmail, email.value.trim());
      if (nativeConsent) setReactChecked(nativeConsent, true);
      const spinStartedAt = window.performance?.now?.() || Date.now();
      subscribeCapture = captureNextSubscribeResponse(7000);
      pending = true;
      submit.disabled = true;
      submit.textContent = text.submitting;
      wrapper.dataset.danielWheelSpinning = 'true';
      startCustomWheel(wrapper);
      setPendingLock();
      nativeSubmitControl.click();
      spinStopTimer = window.setTimeout(() => {
        if (!pending || ui.dataset.resultState) return;
        stopCustomWheel(wrapper);
        submit.textContent = text.processing;
      }, maxSpinMs);

      subscribeCapture.promise.then((response) => {
        if (!pending || ui.dataset.resultState || !response) return;
        const data = response.payload?.data || {};
        const validation = data.validate_results && typeof data.validate_results === 'object'
          ? data.validate_results
          : {};
        const success = response.status === 200
          && Boolean(data.is_success)
          && Object.keys(validation).length === 0;
        if (!success) {
          window.clearTimeout(pendingTimer);
          window.clearTimeout(spinStopTimer);
          stopCustomWheel(wrapper);
          clearPendingLock();
          const emailValidationCodes = new Set(['1110002', '1110004']);
          const emailRejected = Object.keys(validation).some((key) => {
            const normalized = key.replace(/^E/i, '');
            return key.toLowerCase().includes('email') || emailValidationCodes.has(normalized);
          });
          error.textContent = emailRejected
            ? text.emailUnavailable
            : text.submitError;
          pending = false;
          submit.disabled = false;
          submit.textContent = text.submit;
          return;
        }

        const reward = { reward_index: data.reward_index, reward: data.reward };
        const key = resultKey(reward);
        const elapsed = (window.performance?.now?.() || Date.now()) - spinStartedAt;
        const settle = () => {
          if (!pending || ui.dataset.resultState) return;
          const elapsedNow = (window.performance?.now?.() || Date.now()) - spinStartedAt;
          const remaining = maxSpinMs - elapsedNow;
          const showResult = () => {
            window.clearTimeout(pendingTimer);
            window.clearTimeout(spinStopTimer);
            pending = false;
            applyResult(wrapper, ui, reward, key, String(data.discount_code || ''));
          };
          if (remaining <= 180) {
            stopCustomWheel(wrapper);
            showResult();
          } else {
            settleCustomWheel(wrapper, key, showResult, Math.min(900, remaining - 30));
          }
        };
        window.setTimeout(settle, Math.max(0, minSpinMs - elapsed));
      });

      pendingTimer = window.setTimeout(() => {
        if (revealResult(wrapper, ui)) return;
        const nativeError = wrapper.querySelector(selector.nativeContent)?.querySelector(selector.nativeError)?.textContent?.trim();
        subscribeCapture?.cancel();
        window.clearTimeout(spinStopTimer);
        stopCustomWheel(wrapper);
        clearPendingLock();
        error.textContent = nativeError || text.submitError;
        pending = false;
        submit.disabled = false;
        submit.textContent = text.submit;
      }, submitTimeoutMs);
    });

    const nativeObserver = new MutationObserver(() => {
      hideNativeContent(wrapper.querySelector(selector.nativeContent));
      createCustomWheel(wrapper);
      if (revealResult(wrapper, ui)) {
        window.clearTimeout(pendingTimer);
        window.clearTimeout(spinStopTimer);
        subscribeCapture?.cancel();
        pending = false;
        return;
      }
      const nativeError = wrapper.querySelector(selector.nativeContent)?.querySelector(selector.nativeError)?.textContent?.trim();
      if (nativeError && pending) {
        window.clearTimeout(pendingTimer);
        window.clearTimeout(spinStopTimer);
        subscribeCapture?.cancel();
        stopCustomWheel(wrapper);
        clearPendingLock();
        error.textContent = nativeError;
        pending = false;
        submit.disabled = false;
        submit.textContent = text.submit;
      }
    });
    nativeObserver.observe(dialog, { childList: true, subtree: true, characterData: true });
    createCustomWheel(wrapper);
    revealResult(wrapper, ui);

    wrapper.addEventListener('keydown', (event) => trapFocus(wrapper, event));
    wrapper.addEventListener('click', (event) => {
      if (!(event.target instanceof Element) || !event.target.closest(selector.close)) return;
      window.setTimeout(() => {
        document.documentElement.classList.remove('daniel-wheel-open');
        const visual = wrapper.querySelector('.daniel-wheel-visual');
        if (visual) stopShimmer(visual);
      }, 0);
    });
    const wrapperObserver = new MutationObserver(() => {
      const isOpen = isWrapperActuallyOpen(wrapper);
      if (isOpen && !wrapper.classList.contains('daniel-wheel-enhanced')) {
        wrapper.classList.add('daniel-wheel-enhanced');
        document.documentElement.classList.add('daniel-wheel-open');
      }
      const visual = wrapper.querySelector('.daniel-wheel-visual');
      if (isOpen && visual) startShimmer(wrapper, visual);
      const style = window.getComputedStyle(wrapper);
      if (!isOpen || style.display === 'none' || style.visibility === 'hidden') {
        document.documentElement.classList.remove('daniel-wheel-open');
        if (visual) stopShimmer(visual);
      }
    });
    wrapperObserver.observe(wrapper, { attributes: true, attributeFilter: ['class', 'hidden', 'style'] });
    window.setTimeout(() => {
      if (isWrapperActuallyOpen(wrapper)) firstName.focus({ preventScroll: true });
      else document.documentElement.classList.remove('daniel-wheel-open');
    }, 120);
  };

  let scanTimer = 0;
  let bootstrapObserver;
  const scan = () => {
    const wrappers = [...document.querySelectorAll(selector.wrapper)];
    if (!wrappers.length) return false;
    const ready = wrappers.some((wrapper) => (
      wrapper.querySelector(selector.modal)
      && wrapper.querySelector(selector.dialog)
      && wrapper.querySelector(selector.nativeContent)
    ));
    if (!ready) return false;
    bootstrapObserver?.disconnect();
    wrappers.forEach((wrapper) => {
      enhance(wrapper).catch(() => {
        wrapper.classList.remove('daniel-wheel-enhanced');
        document.documentElement.classList.remove('daniel-wheel-open');
        delete wrapper.dataset.danielWheelEnhanced;
      });
    });
    return true;
  };
  const scheduleScan = () => {
    window.clearTimeout(scanTimer);
    scanTimer = window.setTimeout(scan, 80);
  };
  const startBootstrap = () => {
    if (scan()) return;
    bootstrapObserver = new MutationObserver(scheduleScan);
    bootstrapObserver.observe(document.body, { childList: true, subtree: true });
    window.setTimeout(() => bootstrapObserver?.disconnect(), 45000);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startBootstrap, { once: true });
  else startBootstrap();
  window.addEventListener('storage', (event) => {
    if (event.key === config.lockKey && event.newValue) {
      document.querySelectorAll(selector.wrapper).forEach((wrapper) => {
        wrapper.querySelector(selector.close)?.click();
        wrapper.hidden = true;
      });
      document.documentElement.classList.remove('daniel-wheel-open');
    }
  });
  try {
    const channel = new BroadcastChannel('godhund-premium-wheel');
    channel.addEventListener('message', (event) => {
      const message = event.data;
      const isLegacyLock = message === 'locked';
      const isOtherInstanceLock = message?.type === 'locked' && message.source !== instanceId;
      if (!isLegacyLock && !isOtherInstanceLock) return;
      document.querySelectorAll(selector.wrapper).forEach((wrapper) => {
        wrapper.querySelector(selector.close)?.click();
        wrapper.hidden = true;
      });
      document.documentElement.classList.remove('daniel-wheel-open');
    });
  } catch (_) {}

})();
